import { MortgageData } from '@/components/calculator/CalculatorTabs';
import { EnergyEfficiencyImpact } from '@/types/analytics';

/**
 * Calculate payment factor ($/month per $1,000 of home price)
 * This varies based on interest rate
 */
export const calculatePaymentFactor = (interestRate: number): number => {
  const monthlyRate = interestRate / 100 / 12;
  const numberOfPayments = 30 * 12;
  
  if (monthlyRate === 0) return 2.78;
  
  const paymentPer1000 = 1000 * 
    (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  
  return paymentPer1000;
};

/**
 * Estimate annual utility savings based on HERS Index
 * HERS 100 = baseline (standard new home)
 * Lower HERS = more efficient
 */
export const estimateAnnualSavings = (
  hersIndex: number,
  baselineHersIndex: number = 100,
  averageAnnualUtilityCost: number = 2500
): number => {
  if (hersIndex >= baselineHersIndex) return 0;
  
  const efficiencyGain = baselineHersIndex - hersIndex;
  const savingsPercentage = efficiencyGain / baselineHersIndex;
  
  return averageAnnualUtilityCost * savingsPercentage;
};

/**
 * Calculate comprehensive energy efficiency impact
 */
export const calculateEnergyEfficiencyImpact = (
  mortgageData: MortgageData,
  hersIndex: number,
  baselineHersIndex: number = 100,
  averageAnnualUtilityCost: number = 2500
): EnergyEfficiencyImpact => {
  const paymentFactor = calculatePaymentFactor(mortgageData.interestRate);
  
  const annualUtilitySavings = estimateAnnualSavings(
    hersIndex, 
    baselineHersIndex,
    averageAnnualUtilityCost
  );
  const monthlyUtilitySavings = annualUtilitySavings / 12;
  
  const equivalentPriceDifference = (monthlyUtilitySavings / paymentFactor) * 1000;
  
  const adjustedMonthlyPayment = mortgageData.monthlyPayment.total - monthlyUtilitySavings;
  
  const effectiveHomePrice = mortgageData.homePrice - equivalentPriceDifference;
  
  const savingsOverLoanTerm = annualUtilitySavings * mortgageData.loanTerm;
  
  const standardHomePrice = mortgageData.homePrice - equivalentPriceDifference;
  const efficientHomePrice = mortgageData.homePrice;
  const priceDifference = efficientHomePrice - standardHomePrice;
  
  const standardMonthlyMortgage = mortgageData.monthlyPayment.total * 
    (standardHomePrice / efficientHomePrice);
  const standardMonthlyUtilities = (baselineHersIndex / 100) * 
    averageAnnualUtilityCost / 12;
  const totalMonthlyStandard = standardMonthlyMortgage + standardMonthlyUtilities;
  
  const efficientMonthlyUtilities = (hersIndex / 100) * 
    averageAnnualUtilityCost / 12;
  const totalMonthlyEfficient = mortgageData.monthlyPayment.total + efficientMonthlyUtilities;
  
  const netMonthlySavings = totalMonthlyStandard - totalMonthlyEfficient;
  
  return {
    hersIndex,
    baselineHersIndex,
    annualUtilitySavings,
    monthlyUtilitySavings,
    equivalentPriceDifference,
    adjustedMonthlyPayment,
    paymentFactor,
    effectiveHomePrice,
    savingsOverLoanTerm,
    comparisonToStandard: {
      standardHomePrice,
      efficientHomePrice,
      priceDifference,
      totalMonthlyStandard,
      totalMonthlyEfficient,
      netMonthlySavings
    }
  };
};
